import { AfterViewInit, Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { Topbar } from '../topbar';
import { LayoutService, TabCloseEvent } from '@/layout/service/layout.service';
import { AppTopnav } from './app.topnav';
import { AppSidebar } from './app.sidebar';
import { DialogStateService } from '@/share/services/dialog-state-service';
import { CuiToastOutletComponent } from '@idealink-material/ui-core';
import { FormErrorsMessage } from '@/share/component/from-errors-mesage/form-errors-message';
import { SessionMonitorService } from '@/core/service/session-monitor.service';
import { DynamicSessionHandler } from '@/core/template/dynamic-session-handler';
import { SessionStatus } from '@/core/model/session-status';
import { STORAGE_KEYS } from '@/share/constant/storage_token.key';
import { STORAGE_SERVICE_TOKEN } from '@/core/util/storage.token';
import { ActionMenuOverlay } from '@/share/template/button/action-menu-overlay';
import { ProfileDialog } from '@/layout/components/profile-dialog/profile-dialog';
import { DynamicDialog } from '@/share/component/dialog/dynamic-dialog';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopnav, AppSidebar, RouterModule, CuiToastOutletComponent, FormErrorsMessage, DynamicSessionHandler, ActionMenuOverlay, ProfileDialog, DynamicDialog],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <div class="layout-container flex flex-col h-screen bg-[var(--layout-bg-primary)]" [ngClass]="containerClass">
            <app-topnav />
            <div class="flex flex-1 min-h-0">
                <app-sidebar />
                <main class="flex-1 min-w-0 p-6 overflow-y-auto" [class.blurred]="dialogState.dialogOpen()">
                    <div class="layout-content-inner">
                        <router-outlet></router-outlet>
                    </div>
                </main>
            </div>
            <app-dynamic-dialog />
        </div>
        <p-toast-outlet />
        <app-form-errors-message />
        <app-dynamic-session-handler />
        <app-action-menu-overlay />
        <app-profile-dialog />
    `
})
export class AppLayout implements AfterViewInit, OnDestroy {
    tabOpenSubscription: Subscription;

    tabCloseSubscription: Subscription;

    // @ViewChild('dialogOutlet') dialogOutlet!: DynamicDialogComponent;
    private sessionMonitorService = inject(SessionMonitorService);
    private storageService = inject(STORAGE_SERVICE_TOKEN);
    // private readonly storage = inject(STORAGE_SERVICE_TOKEN);

    constructor(
        public layoutService: LayoutService,
        public router: Router,
        public dialogState: DialogStateService
    ) {
        this.tabOpenSubscription = this.layoutService.tabOpen$.subscribe((tab) => {
            this.router.navigate(tab.routerLink);
            this.layoutService.openTab(tab);
        });

        this.tabCloseSubscription = this.layoutService.tabClose$.subscribe((event: TabCloseEvent) => {
            if (
                this.router.isActive(event.tab.routerLink[0], {
                    paths: 'subset',
                    queryParams: 'subset',
                    fragment: 'ignored',
                    matrixParams: 'ignored'
                })
            ) {
                const tabs = this.layoutService.tabs;

                if (tabs.length > 1) {
                    if (event.index === tabs.length - 1) this.router.navigate(tabs[tabs.length - 2].routerLink);
                    else this.router.navigate(tabs[event.index + 1].routerLink);
                } else {
                    this.router.navigate(['/']);
                }
            }

            this.layoutService.closeTab(event.index);
        });
    }

    get containerClass() {
        return {
            'layout-light': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && !this.layoutService.isDarkTheme(),
            'layout-dark': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && this.layoutService.isDarkTheme(),
            'layout-primary': !this.layoutService.isDarkTheme() && this.layoutService.layoutConfig().layoutTheme === 'primaryColor'
        };
    }

    ngOnDestroy() {
        if (this.tabOpenSubscription) {
            this.tabOpenSubscription.unsubscribe();
        }

        if (this.tabCloseSubscription) {
            this.tabCloseSubscription.unsubscribe();
        }
        this.storageService.clearAll();
    }

    ngAfterViewInit(): void {
        // this.dialogMgr.register(this.dialogOutlet);
        const status = this.storageService.get<SessionStatus>(STORAGE_KEYS.SESSION_MONITOR) || SessionStatus.Active;
        this.sessionMonitorService.setStatus(status);
    }
}
